import DynamicForm from '@/components/DynamicForm'
import LoginForm from '@/components/auth/LoginForm'
import {
  Breadcrumbs,
  Button,
  Footer,
  H1,
  Header,
  Main,
  Page,
  Wrap,
} from '@/design-system'
import {
  CreateBookingAppointmentInput,
  MenuItems,
  ServiceStep,
} from '@/generated/graphql'
import { useAuth } from '@/lib/auth'
import { handleHtmlNode } from '@/lib/html'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline'
import {
  add,
  addDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isSameDay,
  isSameMonth,
  parse,
  parseISO,
  startOfDay,
  startOfToday,
} from 'date-fns'
import Link from 'next/link'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import ReactHtmlParser from 'react-html-parser'
import useSWR, { preload } from 'swr'

const fetcher = (url) => fetch(url).then((r) => r.json())

// Constants for wedding appointment papers submission timeframe
const PAPERS_SUBMISSION_MAX_DAYS_BEFORE = 29
const PAPERS_SUBMISSION_MIN_DAYS_BEFORE = 10

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function ProgramareCasatorie({
  menu,
  step,
  papersSubmissionStep,
}: {
  menu: MenuItems[]
  step: ServiceStep
  papersSubmissionStep?: ServiceStep
}) {
  const [today, setToday] = useState(null)
  const [months, setMonths] = useState({})
  const [papersMonths, setPapersMonths] = useState({})
  const [isEditing, setIsEditing] = useState(null)
  const [papersSlotSelected, setPapersSlotSelected] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [createdAppointment, setCreatedAppointment] = useState(null)
  let [selectedDay, setSelectedDay] = useState(startOfToday())
  let [selectedPapersDay, setSelectedPapersDay] = useState(startOfToday())
  let [currentMonth, setCurrentMonth] = useState(
    format(startOfToday(), 'MMM-yyyy')
  )
  let [currentPapersMonth, setCurrentPapersMonth] = useState(
    format(startOfToday(), 'MMM-yyyy')
  )
  let firstDayCurrentMonth = parse(currentMonth, 'MMM-yyyy', new Date())
  let firstDayCurrentPapersMonth = parse(
    currentPapersMonth,
    'MMM-yyyy',
    new Date()
  )
  const year = format(firstDayCurrentMonth, 'yyyy')
  const month = format(firstDayCurrentMonth, 'MM')
  const papersYear = format(firstDayCurrentPapersMonth, 'yyyy')
  const papersMonth = format(firstDayCurrentPapersMonth, 'MM')
  const [globalError, setGlobalError] = useState(null)
  const [nextSlots, setNextSlots] = useState(null)
  const [nextPapersSlots, setNextPapersSlots] = useState(null)
  let nextAvailableInterval = null
  let nextAvailablePapersInterval = null

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  const { isAuthenticated, setIsAuthenticated, profile } = useAuth([
    'first_name',
    'last_name',
    'email',
    'phone_number',
  ])

  const { data: slots, error: slotsError } = useSWR(
    `/api/booking/slots/${step.id}/${year}/${month}`,
    fetcher
  )

  const papersSlotsFetchKey = papersSubmissionStep
    ? `/api/booking/slots/${papersSubmissionStep.id}/${papersYear}/${papersMonth}`
    : null

  const {
    data: papersSlots,
    error: papersSlotsError,
    isLoading: papersSlotsLoading,
  } = useSWR(papersSlotsFetchKey, fetcher)

  const prevCeremonyMonth = add(firstDayCurrentMonth, { months: -1 })
  const prevCeremonyPapersKey = papersSubmissionStep
    ? `/api/booking/slots/${papersSubmissionStep.id}/${format(prevCeremonyMonth, 'yyyy')}/${format(prevCeremonyMonth, 'MM')}`
    : null
  const { data: prevMonthPapersSlots } = useSWR(prevCeremonyPapersKey, fetcher)

  const sameCeremonyPapersKey = papersSubmissionStep
    ? `/api/booking/slots/${papersSubmissionStep.id}/${year}/${month}`
    : null
  const { data: sameCeremonyMonthPapersSlots } = useSWR(sameCeremonyPapersKey, fetcher)

  let days = eachDayOfInterval({
    start: firstDayCurrentMonth,
    end: endOfMonth(firstDayCurrentMonth),
  })

  let papersDays = eachDayOfInterval({
    start: firstDayCurrentPapersMonth,
    end: endOfMonth(firstDayCurrentPapersMonth),
  })

  useEffect(() => {
    const m = {}
    const pM = {}

    const t = startOfToday()
    setToday(t)

    // Process wedding ceremony slots
    slots?.map((slot) => {
      const d = parseISO(slot.start_time)
      if (isBefore(d, t)) return

      if (!m[d.getMonth()]) m[d.getMonth()] = {}
      if (!m[d.getMonth()][d.getDate()]) m[d.getMonth()][d.getDate()] = []
      m[d.getMonth()][d.getDate()].push(slot)
    })

    // Process papers submission slots
    papersSlots?.map((slot) => {
      const d = parseISO(slot.start_time)
      if (isBefore(d, t)) return

      if (!pM[d.getMonth()]) pM[d.getMonth()] = {}
      if (!pM[d.getMonth()][d.getDate()]) pM[d.getMonth()][d.getDate()] = []
      pM[d.getMonth()][d.getDate()].push(slot)
    })

    setMonths(m)
    setPapersMonths(pM)
  }, [slots, papersSlots])

  useEffect(() => {
    const preloadNextMonth = async () => {
      const nextMonth = format(add(firstDayCurrentMonth, { months: 1 }), 'MM')
      const nextYear = format(add(firstDayCurrentMonth, { months: 1 }), 'yyyy')
      const preloadData = await preload(
        `/api/booking/slots/${step.id}/${nextYear}/${nextMonth}`,
        fetcher
      )
      setNextSlots(preloadData)

      // Also preload papers submission slots if available
      if (papersSubmissionStep) {
        const preloadPapersData = await preload(
          `/api/booking/slots/${papersSubmissionStep.id}/${nextYear}/${nextMonth}`,
          fetcher
        )
        setNextPapersSlots(preloadPapersData)
      }
    }

    preloadNextMonth()
  }, [])

  function previousMonth() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 })
    setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'))
  }

  function nextMonth() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 })
    const nextMonth = format(add(firstDayCurrentMonth, { months: 2 }), 'MM')
    const nextYear = format(add(firstDayCurrentMonth, { months: 2 }), 'yyyy')
    preload(`/api/booking/slots/${step.id}/${nextYear}/${nextMonth}`, fetcher)

    // Also preload papers submission slots if available
    if (papersSubmissionStep) {
      preload(
        `/api/booking/slots/${papersSubmissionStep.id}/${nextYear}/${nextMonth}`,
        fetcher
      )
    }

    setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'))
  }

  function previousPapersMonth() {
    let firstDayNextMonth = add(firstDayCurrentPapersMonth, { months: -1 })
    setCurrentPapersMonth(format(firstDayNextMonth, 'MMM-yyyy'))
  }

  function nextPapersMonth() {
    let firstDayNextMonth = add(firstDayCurrentPapersMonth, { months: 1 })
    const nextMonth = format(
      add(firstDayCurrentPapersMonth, { months: 2 }),
      'MM'
    )
    const nextYear = format(
      add(firstDayCurrentPapersMonth, { months: 2 }),
      'yyyy'
    )
    if (papersSubmissionStep) {
      preload(
        `/api/booking/slots/${papersSubmissionStep.id}/${nextYear}/${nextMonth}`,
        fetcher
      )
    }

    setCurrentPapersMonth(format(firstDayNextMonth, 'MMM-yyyy'))
  }

  const createAppointment = async (data) => {
    setSubmitting(true)

    try {
      if (papersSlotSelected && isEditing) {
        // 1. Papers submission appointment
        const papersAppointment: CreateBookingAppointmentInput = {
          slot: { id: papersSlotSelected.id },
          attributes: data,
          service_step: papersSubmissionStep.id,
        }

        const papersRes = await fetch('/api/booking/create', {
          method: 'POST',
          body: JSON.stringify(papersAppointment),
        })

        const papersJson = await papersRes.json()

        if (!papersJson.appointment) {
          setGlobalError(
            papersJson.error ||
              'Eroare la crearea programării pentru depunerea actelor'
          )
          setSubmitting(false)
          return
        }

        // 2. Wedding ceremony appointment
        const ceremonyAppointment: CreateBookingAppointmentInput = {
          slot: { id: isEditing.id },
          attributes: {
            ...data,
            papers_submission_appointment_id: papersJson.appointment.id,
          },
          service_step: step.id,
        }

        const ceremonyRes = await fetch('/api/booking/create', {
          method: 'POST',
          body: JSON.stringify(ceremonyAppointment),
        })

        const ceremonyJson = await ceremonyRes.json()

        if (!ceremonyJson.appointment) {
          setGlobalError(
            ceremonyJson.error ||
              'Eroare la crearea programării pentru oficierea căsătoriei'
          )
          setSubmitting(false)
          return
        }

        setSubmitting(false)
        setIsEditing(false)
        setCreatedAppointment({
          ...ceremonyJson.appointment,
          papers_submission: papersJson.appointment,
        })
      }
    } catch (error) {
      setGlobalError('Eroare la crearea programării')
      setSubmitting(false)
    }
  }

  // Helper function to reset the entire booking flow for wedding services
  const resetBookingFlow = () => {
    setIsEditing(null)
    setPapersSlotSelected(null)
    setGlobalError(null)
  }

  // Helper function to check if a wedding ceremony date has available papers submission slots
  const hasAvailablePapersSubmissionSlots = (weddingDate) => {
    if (!papersSubmissionStep) return true

    const weddingDateOnly = startOfDay(weddingDate)
    const minDate = addDays(weddingDateOnly, -PAPERS_SUBMISSION_MAX_DAYS_BEFORE)
    const maxDate = addDays(weddingDateOnly, -PAPERS_SUBMISSION_MIN_DAYS_BEFORE)

    // Check current month papers slots
    const hasCurrentMonthSlots = papersSlots?.some((slot) => {
      const slotDateOnly = startOfDay(parseISO(slot.start_time))
      return (
        slotDateOnly >= minDate &&
        slotDateOnly <= maxDate &&
        !isBefore(slotDateOnly, today)
      )
    })

    // Check next month papers slots (preloaded)
    const hasNextMonthSlots = nextPapersSlots?.some((slot) => {
      const slotDateOnly = startOfDay(parseISO(slot.start_time))
      return (
        slotDateOnly >= minDate &&
        slotDateOnly <= maxDate &&
        !isBefore(slotDateOnly, today)
      )
    })

    const hasPrevCeremonyMonthSlots = prevMonthPapersSlots?.some((slot) => {
      const slotDateOnly = startOfDay(parseISO(slot.start_time))
      return (
        slotDateOnly >= minDate &&
        slotDateOnly <= maxDate &&
        !isBefore(slotDateOnly, today)
      )
    })

    const hasSameCeremonyMonthSlots = sameCeremonyMonthPapersSlots?.some((slot) => {
      const slotDateOnly = startOfDay(parseISO(slot.start_time))
      return (
        slotDateOnly >= minDate &&
        slotDateOnly <= maxDate &&
        !isBefore(slotDateOnly, today)
      )
    })

    return hasCurrentMonthSlots || hasNextMonthSlots || hasPrevCeremonyMonthSlots || hasSameCeremonyMonthSlots || false
  }

  if (
    !months[selectedDay.getMonth()] ||
    !months[selectedDay.getMonth()][selectedDay.getDate()] ||
    !months[selectedDay.getMonth()][selectedDay.getDate()].some((slot) =>
      hasAvailablePapersSubmissionSlots(parseISO(slot.start_time))
    )
  ) {
    if (slots) {
      for (const slot of slots) {
        if (isBefore(parseISO(slot.start_time), selectedDay)) continue
        if (!hasAvailablePapersSubmissionSlots(parseISO(slot.start_time)))
          continue

        nextAvailableInterval = slot.start_time
        break
      }
    }

    if (!nextAvailableInterval && nextSlots) {
      for (const slot of nextSlots) {
        if (isBefore(parseISO(slot.start_time), selectedDay)) continue
        if (!hasAvailablePapersSubmissionSlots(parseISO(slot.start_time)))
          continue

        nextAvailableInterval = slot.start_time
        break
      }
    }
  }

  // Find next available papers submission interval
  if (papersSubmissionStep) {
    const daySlots =
      papersMonths[selectedPapersDay.getMonth()] &&
      papersMonths[selectedPapersDay.getMonth()][selectedPapersDay.getDate()]

    // Check if there are valid filtered slots for the selected day
    const hasValidSlotsForDay =
      daySlots &&
      daySlots.filter((slot) => {
        if (!isEditing) return true
        const slotDateOnly = startOfDay(parseISO(slot.start_time))
        const weddingDateOnly = startOfDay(parseISO(isEditing.start_time))
        const minDate = addDays(
          weddingDateOnly,
          -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
        )
        const maxDate = addDays(
          weddingDateOnly,
          -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
        )
        return slotDateOnly >= minDate && slotDateOnly <= maxDate
      }).length > 0

    if (!hasValidSlotsForDay) {
      if (papersSlots) {
        for (const slot of papersSlots) {
          if (isBefore(parseISO(slot.start_time), selectedPapersDay)) continue

          // Check if slot is within valid range for wedding
          if (isEditing) {
            const slotDateOnly = startOfDay(parseISO(slot.start_time))
            const weddingDateOnly = startOfDay(parseISO(isEditing.start_time))
            const minDate = addDays(
              weddingDateOnly,
              -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
            )
            const maxDate = addDays(
              weddingDateOnly,
              -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
            )
            if (slotDateOnly < minDate || slotDateOnly > maxDate) continue
          }

          nextAvailablePapersInterval = slot.start_time
          break
        }
      }

      if (!nextAvailablePapersInterval && nextPapersSlots) {
        for (const slot of nextPapersSlots) {
          if (isBefore(parseISO(slot.start_time), selectedPapersDay)) continue

          // Check if slot is within valid range for wedding
          if (isEditing) {
            const slotDateOnly = startOfDay(parseISO(slot.start_time))
            const weddingDateOnly = startOfDay(parseISO(isEditing.start_time))
            const minDate = addDays(
              weddingDateOnly,
              -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
            )
            const maxDate = addDays(
              weddingDateOnly,
              -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
            )
            if (slotDateOnly < minDate || slotDateOnly > maxDate) continue
          }

          nextAvailablePapersInterval = slot.start_time
          break
        }
      }

      if (!nextAvailablePapersInterval && sameCeremonyMonthPapersSlots) {
        for (const slot of sameCeremonyMonthPapersSlots) {
          if (isBefore(parseISO(slot.start_time), selectedPapersDay)) continue

          if (isEditing) {
            const slotDateOnly = startOfDay(parseISO(slot.start_time))
            const weddingDateOnly = startOfDay(parseISO(isEditing.start_time))
            const minDate = addDays(
              weddingDateOnly,
              -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
            )
            const maxDate = addDays(
              weddingDateOnly,
              -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
            )
            if (slotDateOnly < minDate || slotDateOnly > maxDate) continue
          }

          nextAvailablePapersInterval = slot.start_time
          break
        }
      }
    }
  }

  if (!step) {
    return <div>No step</div>
  }

  return (
    <Page>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY}`}
      />
      <Header menu={menu}></Header>
      <Wrap>
        {process.env.NEXT_PUBLIC_EVPERS == 'true' ||
          process.env.NEXT_PUBLIC_SERVICII == 'true' || (
            <Breadcrumbs>
              <Link href="/">Acasă</Link>
              <Link href="/servicii">Servicii</Link>
              <Link href={`/servicii/categorie/${step.service.category.slug}`}>
                {step.service.category.name}
              </Link>
              <Link href={`/servicii/${step.service.slug}`}>
                {step.service.name}
              </Link>
            </Breadcrumbs>
          )}
        {(process.env.NEXT_PUBLIC_EVPERS == 'true' ||
          process.env.NEXT_PUBLIC_SERVICII == 'true') && (
          <div className="mt-8"></div>
        )}
        {(!isEditing || isAuthenticated) && (
          <>
            <H1 className="">
              Programare {step.service.name.toLowerCase()}{' '}
              {step.service.slug == 'act-identitate' && ' format vechi (1997)'}
            </H1>
            <Header.Description>
              <>
                {ReactHtmlParser(step.service.description, {
                  transform: handleHtmlNode,
                })}
              </>
            </Header.Description>
          </>
        )}
      </Wrap>

      <Main>
        {isEditing && !isAuthenticated ? (
          <div className="-mt-9">
            <LoginForm
              onLogin={() => {
                setIsAuthenticated(true)
              }}
              onCancel={() => {
                setIsEditing(false)
              }}
              profileFields={{
                cnp: null,
                email: { required: true },
              }}
            />
          </div>
        ) : createdAppointment ? (
          <>
            <div className="max-w-2xl p-8 text-white bg-green-700">
              <h2 className="text-3xl font-bold border-0">
                {createdAppointment.papers_submission
                  ? 'Programări create cu succes'
                  : 'Programare creată cu succes'}
              </h2>

              {createdAppointment.papers_submission ? (
                <div className="mt-8 space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-2">
                      1. Depunerea actelor
                    </h3>
                    <div className="text-lg">
                      Data:{' '}
                      {format(
                        parseISO(
                          createdAppointment.papers_submission.start_time
                        ),
                        'EEEE d MMMM yyy'
                      )}
                      <br />
                      Ora:{' '}
                      {format(
                        parseISO(
                          createdAppointment.papers_submission.start_time
                        ),
                        'HH:mm'
                      )}
                      <br />
                      Locație:{' '}
                      {createdAppointment.papers_submission.slot.location.name}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-2">
                      2. Oficierea căsătoriei
                    </h3>
                    <div className="text-lg">
                      Data:{' '}
                      {format(
                        parseISO(createdAppointment.start_time),
                        'EEEE d MMMM yyy'
                      )}
                      <br />
                      Ora:{' '}
                      {format(parseISO(createdAppointment.start_time), 'HH:mm')}
                      <br />
                      Locație: {createdAppointment.slot.location.name}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-8 text-xl text-left">
                  Data:{' '}
                  {format(
                    parseISO(createdAppointment.start_time),
                    'EEEE d MMMM yyy'
                  )}
                  <br />
                  Ora:{' '}
                  {format(parseISO(createdAppointment.start_time), 'HH:mm')}
                  <br />
                  Locație: {createdAppointment.slot.location.name}
                </div>
              )}
            </div>
            <div className="my-4">
              Pentru anularea programării, vizitați secțiunea{' '}
              <Link
                href="/cont"
                className="text-primary hover:text-primary-dark"
              >
                Contul meu
              </Link>
              .
            </div>
          </>
        ) : (
          <div className="-mt-4 sm:mt-0">
            <div className="max-w-md md:max-w-5xl">
              {papersSubmissionStep ? (
                <div className="space-y-8">
                  {/* Step indicator */}
                  <div className="flex items-center justify-between bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          isEditing
                            ? 'bg-green-600 text-white'
                            : 'bg-primary text-white'
                        }`}
                      >
                        1
                      </div>
                      <span
                        className={
                          isEditing
                            ? 'text-green-600 font-semibold'
                            : 'text-primary font-semibold'
                        }
                      >
                        Oficiere căsătorie
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          isEditing && papersSlotSelected
                            ? 'bg-green-600 text-white'
                            : isEditing && !papersSlotSelected
                            ? 'bg-primary text-white'
                            : 'bg-gray-300 text-gray-600'
                        }`}
                      >
                        2
                      </div>
                      <span
                        className={
                          isEditing && papersSlotSelected
                            ? 'text-green-600 font-semibold'
                            : isEditing && !papersSlotSelected
                            ? 'text-primary font-semibold'
                            : 'text-gray-600'
                        }
                      >
                        Depunere acte
                      </span>
                    </div>
                  </div>

                  {/* Wedding ceremony calendar - shown first */}
                  {!isEditing && (
                    <div>
                      <h3 className="text-2xl font-semibold mb-8">
                        Pas 1: Alegeți data și ora pentru oficierea căsătoriei
                      </h3>
                    </div>
                  )}

                  {/* Papers submission calendar - shown after wedding date is selected */}
                  {isEditing && !papersSlotSelected && (
                    <div>
                      <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div>
                            <p className="text-green-700">
                              Oficierea căsătoriei aleasă:{' '}
                              <strong>
                                {format(
                                  parseISO(isEditing.start_time),
                                  'EEEE, d MMMM yyyy, HH:mm'
                                )}
                              </strong>
                            </p>
                            <p className="text-green-700 mt-1">
                              Actele trebuie depuse{' '}
                              <strong>
                                între{' '}
                                {format(
                                  addDays(
                                    parseISO(isEditing.start_time),
                                    -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
                                  ),
                                  'd MMMM'
                                )}{' '}
                                și{' '}
                                {format(
                                  addDays(
                                    parseISO(isEditing.start_time),
                                    -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
                                  ),
                                  'd MMMM yyyy'
                                )}
                              </strong>{' '}
                              <small>
                                (între a 11-a zi și a 30-a zi de la depunere)
                              </small>
                            </p>
                          </div>
                          <Button
                            onClick={() => resetBookingFlow()}
                            className="text-sm md:flex-shrink-0 md:self-start"
                          >
                            Schimbă data căsătoriei
                          </Button>
                        </div>
                      </div>
                      <h3 className="text-2xl font-semibold mb-8">
                        Pas 2: Alegeți data și ora pentru depunerea actelor
                      </h3>
                      <div className="md:grid md:grid-cols-2 lg:grid-cols-5 md:divide-x md:divide-gray-200">
                        {!papersSlotSelected && (
                          <div className="md:pr-14 lg:col-span-2">
                            <div
                              className="flex items-center pt-3"
                              id="papers-calendar"
                            >
                              <h2 className="flex-auto font-semibold text-gray-900 capitalize text-xl sm:text-2xl tracking-tight">
                                {format(
                                  firstDayCurrentPapersMonth,
                                  'MMMM yyyy'
                                )}
                              </h2>
                              <button
                                type="button"
                                onClick={previousPapersMonth}
                                className="-my-1.5 flex flex-none items-center justify-center p-1.5 rounded-md text-primary hover:bg-primary hover:text-white"
                              >
                                <span className="sr-only">Previous month</span>
                                <ChevronLeftIcon
                                  className="w-8 h-8"
                                  aria-hidden="true"
                                />
                              </button>
                              <button
                                onClick={nextPapersMonth}
                                type="button"
                                className="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-1.5 rounded-md text-primary hover:bg-primary hover:text-white"
                              >
                                <span className="sr-only">Next month</span>
                                <ChevronRightIcon
                                  className="w-8 h-8"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                            <div className="grid grid-cols-7 gap-2 mt-4 md:mt-6 leading-6 text-center font-semibold">
                              <div>Lu</div>
                              <div>Ma</div>
                              <div>Mi</div>
                              <div>Jo</div>
                              <div>Vi</div>
                              <div>Sâ</div>
                              <div>Du</div>
                            </div>
                            <div className="grid grid-cols-7 mt-2 gap-2">
                              {papersDays.map((day, dayIdx) => (
                                <div
                                  key={day.toString()}
                                  className={classNames(
                                    dayIdx === 0 &&
                                      colStartClasses[getDay(day)],
                                    ''
                                  )}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      const el =
                                        document.getElementById(
                                          'papers-calendar'
                                        )
                                      if (window.scrollY < el.offsetTop) {
                                        el.scrollIntoView({
                                          behavior: 'smooth',
                                        })
                                      }
                                      setSelectedPapersDay(day)
                                    }}
                                    disabled={
                                      isEditing &&
                                      (isBefore(
                                        startOfDay(day),
                                        addDays(
                                          startOfDay(
                                            parseISO(isEditing.start_time)
                                          ),
                                          -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
                                        )
                                      ) ||
                                        isBefore(
                                          addDays(
                                            startOfDay(
                                              parseISO(isEditing.start_time)
                                            ),
                                            -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
                                          ),
                                          startOfDay(day)
                                        ))
                                    }
                                    className={classNames(
                                      isSameDay(day, selectedPapersDay) &&
                                        'text-white',
                                      !isSameDay(day, selectedPapersDay) &&
                                        isSameDay(day, today) &&
                                        'text-red-500',
                                      papersMonths[day.getMonth()] &&
                                        papersMonths[day.getMonth()][
                                          day.getDate()
                                        ] &&
                                        !isSameDay(day, selectedPapersDay) &&
                                        !(
                                          isEditing &&
                                          (isBefore(
                                            startOfDay(day),
                                            addDays(
                                              startOfDay(
                                                parseISO(isEditing.start_time)
                                              ),
                                              -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
                                            )
                                          ) ||
                                            isBefore(
                                              addDays(
                                                startOfDay(
                                                  parseISO(isEditing.start_time)
                                                ),
                                                -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
                                              ),
                                              startOfDay(day)
                                            ))
                                        ) &&
                                        'text-primary font-bold border-2 border-primary hover:text-white hover:bg-primary',
                                      (!today ||
                                        !isSameDay(day, selectedPapersDay)) &&
                                        !isSameDay(day, today) &&
                                        isSameMonth(
                                          day,
                                          firstDayCurrentPapersMonth
                                        ) &&
                                        (!papersMonths[day.getMonth()] ||
                                          !papersMonths[day.getMonth()][
                                            day.getDate()
                                          ]) &&
                                        'text-gray-300',
                                      !isSameDay(day, selectedPapersDay) &&
                                        !isSameDay(day, today) &&
                                        !isSameMonth(
                                          day,
                                          firstDayCurrentPapersMonth
                                        ) &&
                                        'text-gray-400',
                                      isSameDay(day, selectedPapersDay) &&
                                        isSameDay(day, today) &&
                                        'bg-red-500',
                                      isSameDay(day, selectedPapersDay) &&
                                        today &&
                                        !isSameDay(day, today) &&
                                        'bg-gray-900',
                                      ((today &&
                                        isSameDay(day, selectedPapersDay)) ||
                                        isSameDay(day, today)) &&
                                        'font-semibold',
                                      // Disabled styling for dates outside valid range
                                      isEditing &&
                                        (isBefore(
                                          startOfDay(day),
                                          addDays(
                                            startOfDay(
                                              parseISO(isEditing.start_time)
                                            ),
                                            -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
                                          )
                                        ) ||
                                          isBefore(
                                            addDays(
                                              startOfDay(
                                                parseISO(isEditing.start_time)
                                              ),
                                              -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
                                            ),
                                            startOfDay(day)
                                          )) &&
                                        'opacity-50 cursor-not-allowed',
                                      'mx-auto flex h-10 w-10 items-center justify-center rounded-lg'
                                    )}
                                  >
                                    <time dateTime={format(day, 'yyyy-MM-dd')}>
                                      {format(day, 'd')}
                                    </time>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <section className="mt-12 md:mt-0 md:pl-14 lg:col-span-3">
                          <h2 className="font-semibold text-gray-900">
                            <time
                              dateTime={format(selectedPapersDay, 'yyyy-MM-dd')}
                            >
                              <span className="capitalize">
                                {format(selectedPapersDay, 'EEEE')}
                              </span>{' '}
                              {format(selectedPapersDay, 'd MMMM yyy')}
                            </time>
                          </h2>
                          <ol className="-mt-2 text-gray-500 ml-0">
                            {(() => {
                              const daySlots =
                                papersMonths[selectedPapersDay.getMonth()] &&
                                papersMonths[selectedPapersDay.getMonth()][
                                  selectedPapersDay.getDate()
                                ]

                              if (!daySlots) return false

                              // Filter slots to only show valid ones (within PAPERS_SUBMISSION timeframe before wedding)
                              const validSlots = daySlots.filter((slot) => {
                                if (!isEditing) return true
                                const slotDateOnly = startOfDay(
                                  parseISO(slot.start_time)
                                )
                                const weddingDateOnly = startOfDay(
                                  parseISO(isEditing.start_time)
                                )
                                const minDate = addDays(
                                  weddingDateOnly,
                                  -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
                                )
                                const maxDate = addDays(
                                  weddingDateOnly,
                                  -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
                                )
                                return (
                                  slotDateOnly >= minDate &&
                                  slotDateOnly <= maxDate
                                )
                              })

                              return validSlots.length > 0
                            })() ? (
                              <div className="space-y-3">
                                {papersMonths[selectedPapersDay.getMonth()][
                                  selectedPapersDay.getDate()
                                ]
                                  .filter((slot) => {
                                    if (!isEditing) return true
                                    const slotDateOnly = startOfDay(
                                      parseISO(slot.start_time)
                                    )
                                    const weddingDateOnly = startOfDay(
                                      parseISO(isEditing.start_time)
                                    )
                                    const minDate = addDays(
                                      weddingDateOnly,
                                      -PAPERS_SUBMISSION_MAX_DAYS_BEFORE
                                    )
                                    const maxDate = addDays(
                                      weddingDateOnly,
                                      -PAPERS_SUBMISSION_MIN_DAYS_BEFORE
                                    )
                                    return (
                                      slotDateOnly >= minDate &&
                                      slotDateOnly <= maxDate
                                    )
                                  })
                                  .map((slot) => (
                                    <Button
                                      small
                                      className="border-2 text-base w-20 mr-3"
                                      key={slot.start_time}
                                      onClick={() => {
                                        setPapersSlotSelected(slot)
                                      }}
                                    >
                                      {format(
                                        parseISO(slot.start_time),
                                        'HH:mm'
                                      )}
                                    </Button>
                                  ))}
                              </div>
                            ) : (
                              <>
                                <p className="pt-2">
                                  Niciun interval disponibil în această zi.
                                </p>
                                {nextAvailablePapersInterval && (
                                  <>
                                    <p className="mb-2">
                                      Următorul interval disponibil:{' '}
                                    </p>
                                    <p>
                                      <Button
                                        arrow
                                        primary
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setCurrentPapersMonth(
                                            format(
                                              parseISO(
                                                nextAvailablePapersInterval
                                              ),
                                              'MMM-yyyy'
                                            )
                                          )
                                          setSelectedPapersDay(
                                            parseISO(
                                              nextAvailablePapersInterval
                                            )
                                          )
                                        }}
                                      >
                                        <span className="capitalize">
                                          {format(
                                            parseISO(
                                              nextAvailablePapersInterval
                                            ),
                                            'EEEE'
                                          )}
                                        </span>{' '}
                                        {format(
                                          parseISO(nextAvailablePapersInterval),
                                          'd MMMM yyy'
                                        )}
                                        , ora{' '}
                                        {format(
                                          parseISO(nextAvailablePapersInterval),
                                          'HH:mm'
                                        )}
                                      </Button>
                                    </p>
                                  </>
                                )}
                              </>
                            )}
                          </ol>
                        </section>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  <strong className="font-bold">Eroare: </strong>
                  <span className="block sm:inline">
                    Nu s-a putut încărca informația pentru depunerea actelor. Vă
                    rugăm să încercați din nou mai târziu.
                  </span>
                </div>
              )}

              {/* Show alert when both wedding and papers slots are selected */}
              {isEditing && papersSlotSelected && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4 mt-8">
                  <p className="text-green-700 mt-1">
                    <strong>Oficierea căsătoriei:</strong>{' '}
                    {format(
                      parseISO(isEditing.start_time),
                      'EEEE, d MMMM yyyy, HH:mm'
                    )}
                  </p>
                  <p className="text-green-700">
                    <strong>Depunerea actelor:</strong>{' '}
                    {format(
                      parseISO(papersSlotSelected.start_time),
                      'EEEE, d MMMM yyyy, HH:mm'
                    )}
                  </p>
                </div>
              )}

              {/* Regular calendar or wedding ceremony calendar */}
              {(!isEditing || papersSlotSelected) && (
                <div className="md:grid md:grid-cols-2 lg:grid-cols-5 md:divide-x md:divide-gray-200">
                  <div className="md:pr-14 lg:col-span-2">
                    <div className="flex items-center pt-3" id="calendar">
                      <h2 className="flex-auto font-semibold text-gray-900 capitalize text-xl sm:text-2xl tracking-tight">
                        {format(firstDayCurrentMonth, 'MMMM yyyy')}
                      </h2>
                      <button
                        type="button"
                        onClick={previousMonth}
                        className="-my-1.5 flex flex-none items-center justify-center p-1.5 rounded-md text-primary hover:bg-primary hover:text-white"
                      >
                        <span className="sr-only">Previous month</span>
                        <ChevronLeftIcon
                          className="w-8 h-8"
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        onClick={nextMonth}
                        type="button"
                        className="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-1.5 rounded-md text-primary hover:bg-primary hover:text-white"
                      >
                        <span className="sr-only">Next month</span>
                        <ChevronRightIcon
                          className="w-8 h-8"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-2 mt-4 md:mt-6 leading-6 text-center font-semibold">
                      <div>Lu</div>
                      <div>Ma</div>
                      <div>Mi</div>
                      <div>Jo</div>
                      <div>Vi</div>
                      <div>Sâ</div>
                      <div>Du</div>
                    </div>
                    <div className="grid grid-cols-7 mt-2 gap-2">
                      {days.map((day, dayIdx) => (
                        <div
                          key={day.toString()}
                          className={classNames(
                            dayIdx === 0 && colStartClasses[getDay(day)],
                            ''
                          )}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              const el = document.getElementById('calendar')
                              if (window.scrollY < el.offsetTop) {
                                el.scrollIntoView({
                                  behavior: 'smooth',
                                })
                              }
                              setSelectedDay(day)
                              setIsEditing(null)
                            }}
                            className={classNames(
                              isSameDay(day, selectedDay) && 'text-white',
                              !isSameDay(day, selectedDay) &&
                                isSameDay(day, today) &&
                                'text-red-500',
                              months[day.getMonth()] &&
                                months[day.getMonth()][day.getDate()] &&
                                months[day.getMonth()][day.getDate()].some(
                                  (slot) =>
                                    hasAvailablePapersSubmissionSlots(
                                      parseISO(slot.start_time)
                                    )
                                ) &&
                                !isSameDay(day, selectedDay) &&
                                'text-primary font-bold border-2 border-primary hover:text-white hover:bg-primary',
                              (!today || !isSameDay(day, selectedDay)) &&
                                !isSameDay(day, today) &&
                                isSameMonth(day, firstDayCurrentMonth) &&
                                (!months[day.getMonth()] ||
                                  !months[day.getMonth()][day.getDate()] ||
                                  !months[day.getMonth()][day.getDate()].some(
                                    (slot) =>
                                      hasAvailablePapersSubmissionSlots(
                                        parseISO(slot.start_time)
                                      )
                                  )) &&
                                'text-gray-300',
                              !isSameDay(day, selectedDay) &&
                                !isSameDay(day, today) &&
                                !isSameMonth(day, firstDayCurrentMonth) &&
                                'text-gray-400',
                              isSameDay(day, selectedDay) &&
                                isSameDay(day, today) &&
                                'bg-red-500',
                              isSameDay(day, selectedDay) &&
                                today &&
                                !isSameDay(day, today) &&
                                'bg-gray-900',
                              ((today && isSameDay(day, selectedDay)) ||
                                isSameDay(day, today)) &&
                                'font-semibold',
                              'mx-auto flex h-10 w-10 items-center justify-center rounded-lg'
                            )}
                          >
                            <time dateTime={format(day, 'yyyy-MM-dd')}>
                              {format(day, 'd')}
                            </time>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <section className="mt-12 md:mt-0 md:pl-14 lg:col-span-3">
                    <h2 className="font-semibold text-gray-900">
                      <time dateTime={format(selectedDay, 'yyyy-MM-dd')}>
                        <span className="capitalize">
                          {format(selectedDay, 'EEEE')}
                        </span>{' '}
                        {format(selectedDay, 'd MMMM yyy')}
                        {isEditing && (
                          <span>
                            , ora{' '}
                            {format(parseISO(isEditing.start_time), 'HH:mm')}
                          </span>
                        )}
                      </time>
                    </h2>

                    {isEditing
                      ? isAuthenticated && (
                          <form
                            onSubmit={handleSubmit(createAppointment)}
                            className="mb-8"
                          >
                            {globalError && (
                              <div className="bg-red-100 p-3 mb-2 text-red-800 rounded-md">
                                {globalError}
                              </div>
                            )}
                            <div className="bg-slate-100 px-4 pb-6 grid grid-cols-1 md:grid-cols-2 gap-x-4 border-b-4 border-primary">
                              <DynamicForm
                                fields={step.form.fields}
                                register={register}
                                profile={profile}
                                errors={errors}
                                setValue={setValue}
                              />
                            </div>
                            <div className="mt-4 flex flex-col sm:flex-row gap-4">
                              <Button
                                primary
                                disabled={submitting}
                                className="max-w-none"
                              >
                                {submitting && (
                                  <svg
                                    className="w-5 h-5 mr-2 text-white animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx={12}
                                      cy={12}
                                      r={10}
                                      stroke="currentColor"
                                      strokeWidth={4}
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                  </svg>
                                )}
                                Creează programarea
                              </Button>
                              <Button
                                className="max-w-none"
                                onClick={resetBookingFlow}
                              >
                                Renunță
                              </Button>
                            </div>
                          </form>
                        )
                      : !createdAppointment && (
                          <ol className="-mt-2 text-gray-500 ml-0">
                            {months[selectedDay.getMonth()] &&
                            months[selectedDay.getMonth()][
                              selectedDay.getDate()
                            ] &&
                            months[selectedDay.getMonth()][
                              selectedDay.getDate()
                            ].filter((slot) =>
                              hasAvailablePapersSubmissionSlots(
                                parseISO(slot.start_time)
                              )
                            ).length > 0 ? (
                              <div className="space-y-3">
                                {months[selectedDay.getMonth()][
                                  selectedDay.getDate()
                                ]
                                  .filter((slot) =>
                                    hasAvailablePapersSubmissionSlots(
                                      parseISO(slot.start_time)
                                    )
                                  )
                                  .map((slot) => (
                                    <Button
                                      small
                                      className="border-2 text-base w-20 mr-3"
                                      key={slot.start_time}
                                      onClick={() => {
                                        setIsEditing(slot)
                                        setPapersSlotSelected(null)
                                      }}
                                    >
                                      {format(
                                        parseISO(slot.start_time),
                                        'HH:mm'
                                      )}
                                    </Button>
                                  ))}
                              </div>
                            ) : (
                              <>
                                <p className="pt-2">
                                  Niciun interval disponibil în această zi.
                                </p>
                                {nextAvailableInterval && (
                                  <>
                                    <p className="mb-2">
                                      Următorul interval disponibil:{' '}
                                    </p>
                                    <p>
                                      <Button
                                        arrow
                                        primary
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setCurrentMonth(
                                            format(
                                              parseISO(nextAvailableInterval),
                                              'MMM-yyyy'
                                            )
                                          )
                                          setSelectedDay(
                                            parseISO(nextAvailableInterval)
                                          )
                                        }}
                                      >
                                        <span className="capitalize">
                                          {format(
                                            parseISO(nextAvailableInterval),
                                            'EEEE'
                                          )}
                                        </span>{' '}
                                        {format(
                                          parseISO(nextAvailableInterval),
                                          'd MMMM yyy'
                                        )}
                                        , ora{' '}
                                        {format(
                                          parseISO(nextAvailableInterval),
                                          'HH:mm'
                                        )}
                                      </Button>
                                    </p>
                                  </>
                                )}
                              </>
                            )}
                          </ol>
                        )}
                  </section>
                </div>
              )}
            </div>
          </div>
        )}
      </Main>

      <Footer feedbackForm={false} />
    </Page>
  )
}

let colStartClasses = [
  'col-start-7',
  'col-start-1',
  'col-start-2',
  'col-start-3',
  'col-start-4',
  'col-start-5',
  'col-start-6',
]
